// Short captions for every decorative combo — AUTHORED DATA.
//
// One one-line caption per ornament, nested by collection slug then by the exact
// combo string from src/data/decorative.ts. (Re)authored out-of-band with
// scripts/gen-combo-descriptions.mjs (DeepSeek) and committed as plain data —
// the build itself is deterministic and offline (no API, no network).
//
// scripts/validate.mjs §29 is the gate: every combo must have a non-empty
// caption, captions must be unique within their collection, and no fabricated
// stats / popularity / platform names may appear.

export const COMBO_DESCRIPTIONS: Record<string, Record<string, string>> = {
  "borders": {
    "╭┈┈┈┈╮\n╰┈┈┈┈╯": "A delicate open frame for a short text or name.",
    "╭─────╮\n╰─────╯": "Minimalist corner frame with open sides.",
    "┌──────┐\n└──────┘": "Neat minimal frame, clean and understated.",
    "╒══════╕\n╘══════╛": "Thick single-line border frame with corner joints.",
    "╔══════╗\n╚══════╝": "A clean double-line frame for a short title or name.",
    "╔══•●•══╗\n╚══•●•══╝": "Flowing dots framed by symmetrical double-line borders.",
    "┌── •✧• ──┐\n└── •✧• ──┘": "Delicate star-like dots framed by clean lines.",
    "╔═.✾. ════╗\n╚════.✾. ═╝": "A symmetrical border with dotted corners and a gentle floral accent.",
    "┌── ⋆⋅☆⋅⋆ ──┐\n└── ⋆⋅☆⋅⋆ ──┘": "A starry framed box with a soft, whimsical feel.",
    "╭──〔❨✧✧❩〕──╮\n╰──〔❨✧✧❩〕──╯": "Delicate sparkles framed by rounded brackets in a neat symmetrical border.",
    "┌─── ∘°❉°∘ ───┐\n└─── °∘❉∘° ───┘": "Elegant symmetrical border framing a name or title.",
    "╔══《✧》══╗\n╚══《✧》══╝": "A symmetrical double-line border with star motifs at each corner.",
    "┏━✦❘༻༺❘✦━━┓\n┗━✦❘༻༺❘✦━━┛": "Symmetrical frame with a diamond-and-petal center.",
    "┍━☽【❖】☾━┑\n┕━☽【❖】☾━┙": "Symmetrical diamond frame with star center and crescent moon corners.",
    "╔══ஓ๑♡๑ஓ══╗\n╚══ஓ๑♡๑ஓ══╝": "A sweet, symmetrical heart border for a gentle title.",
    "┏━━━━━༻❁༺━━━━━┓\n┗━━━━━༻❁༺━━━━━┛": "Diamond flower centered in a double-lined bordered box.",
    "╭━━━━━∙⋆⋅⋆∙━━━━━╮\n╰━━━━━∙⋆⋅⋆∙━━━━━╯": "A rounded starburst frame for a short name or title.",
    "╔══❖═══════❖══╗\n╚══❖═══════❖══╝": "A diamond-capped double-line frame with balanced symmetry.",
    "╔═══✿════════✿═══╗\n╚═══✿════════✿═══╝": "Floral corner brackets framing a gentle, symmetrical border.",
    "╔═══════☆♡☆═══════╗\n╚═══════☆♡☆═══════╝": "A symmetrical heart-and-star bordered box frame.",
    "╔══ ❀•°❀°•❀ ══╗\n╚══ ❀•°❀°•❀ ══╝": "A soft floral frame with a gentle, romantic feel.",
    "┏━•❃°•°❀°•°❃•━┓\n┗━•❃°•°❀°•°❃•━┛": "Delicate floral pattern framed by dotted diamond border.",
    "┌─────━┿──┿━─────┐\n└─────━┿──┿━─────┘": "A neat frame with inner dividers for a name or title.",
    "╔════ ✿ ❀ ღღ ✿ ❀ ════╗\n╚════ ❀ ✿ ღღ ❀ ✿ ════╝": "A flower-dotted double-line rectangle with gentle symmetry.",
    "┏━━━━━•❅•°•❈•°•❅•━━━━━┓\n┗━━━━━•❅•°•❈•°•❅•━━━━━┛": "A symmetrical border with a starlike centre framed by diamonds.",
    "┌──═━┈━═──┐\n└──═━┈━═──┘": "A neat symmetrical bordered frame with corner accents.",
    "╔══ ≪ °❈° ≫ ══╗\n╚══ ≪ °❈° ≫ ══╝": "A delicate floral border wrapping your text in gentle symmetry.",
    "╭ ─┉─ • ─┉─ ╮\n╰ ─┉─ • ─┉─ ╯": "Soft dotted lines meet at a centered diamond.",
    "╔══════✮❁•°♛°•❁✮══════╗\n╚══════✮❁•°❀°•❁✮══════╝": "Regal crown-shaped border for a royal name or title.",
    "┏━━━━━•°•°•❈•°•°•━━━━━┓\n┗━━━━━•°•°•❈•°•°•━━━━━┛": "A bordered frame with dotted stars and diamond accents.",
    "╔══✿══════✿══╗\n╚══✿══════✿══╝": "Flower-studded double border with symmetrical corner blooms.",
    "╔═══ ✧ ═══╗\n╚═══ ✧ ═══╝": "A symmetrical double-line frame with a star at center top and bottom.",
    "┌─❀─────❀─┐\n└─❀─────❀─┘": "Simple floral border with a gentle, romantic frame.",
    "╭⋆⋅☆⋅⋆⋆⋅☆⋅⋆╮\n╰⋆⋅☆⋅⋆⋆⋅☆⋅⋆╯": "A star-dotted frame with mirrored tops and bottoms."
  },
  "dividers": {
    "──────────": "A classic divider to frame a name or title.",
    "︶︶︶︶︶︶︶︶︶": "A gentle, repeated downward curve, like a calm wave settling across the line.",
    "⌣ ‿ ‿ ‿ ‿ ‿ ‿ ⌣": "A gentle wave of dots cradled by curved brackets.",
    "─── ⋆⋅☆⋅⋆ ───": "A symmetrical star centered between thin horizontal lines.",
    "∘₊✧──────✧₊∘": "Delicate spark line with soft starry punctuation.",
    "•┈••✦ ❤ ✦••┈•": "Hearts framed by dotted lines and sparkles.",
    "───※ ·❆· ※───": "Sharp snowflake flanked by bars for a clean bio break.",
    "•❅───✧❅✦❅✧───❅•": "Crystal shards linked by sharp dots across a snowy line.",
    "-ˏˋ✄┈┈┈┈": "A soft cut between sections with a delicate ribbon of dashes.",
    "❀° ── ⋅ ── ✩ ── ⋅ ──": "Delicate flower and star motif balanced by fine dashes.",
    "────୨ৎ────": "A soft, symmetrical divider with a delicate heart accent.",
    "─── ✯ ━━━━ ✿ ━━━━ ✯ ───": "A starry floral divider balanced by symmetrical sparkles.",
    "·˚ ༘₊·꒰➳: ̗̀➛": "Delicate floral divider for a name or bio header.",
    "꒷꒦︶꒷꒦︶꒷꒦": "Delicate petal-like marks spaced across a gentle divider.",
    "✦•┈๑⋅⋯ ⋯⋅๑┈•✦": "Twin stars linked by a delicate dotted chain.",
    "╴╴╴╴╴╴ ⚘ ╴╴╴╴╴╴": "A centered floral dot balanced between two even lines.",
    "⋆˙⟡ ─────── ⟡˙⋆": "Whimsical star-dusted line break for dreamy sections.",
    "꒰⊹ ───── ⊹꒱": "Delicate bow-tie divider with soft wing ends.",
    "•°. ─────── .°•": "A soft dotted line divider for a name or bio.",
    "❥─────────────": "Sleek heart-topped line for elegant section breaks.",
    "│ ✦ │ ─────────": "A delicate star framed by thin vertical lines and a long dash.",
    "︶︶︶︶ ✿ ︶︶︶︶": "Delicate floral centerpiece framed by symmetrical curved lines.",
    "•❣•୨୧┈┈┈୨୧•❣•": "Whimsical pink hearts framing a soft centered divider.",
    "჻჻჻჻჻჻჻჻჻჻჻჻": "A long chain of mirrored arrows.",
    "❀° ┄───╮ ╰───┄ °❀": "Delicate floral divider for names or section titles.",
    "─━━━━━━⊱✫⊰━━━━━━─": "A symmetrical divider with a starry center and solid edges.",
    "═══ ✧ ═══": "A single diamond star framed by double horizontal lines.",
    "⊰ ━━━━━━ ⊱": "Symmetric double-arrow divider with sharp, centered lines.",
    "•°• ─────── •°•": "A soft, centered divider with tiny dots and a clean line.",
    "⋆ ━━━━━ ⋆ ━━━━━ ⋆": "Elegant star-anchored line breaks for a polished section divider.",
    "❍ ─────── ❍": "A sleek divider with dots, perfect for centering a name in a bio.",
    "˚ ༘ ⋆｡˚ ─── ˚｡⋆ ༘ ˚": "A delicate, dotted line with soft, floating stars."
  },
  "loading-bars": {
    "█▒▒▒▒▒▒▒▒▒ 10%": "A thin loading bar starting at ten percent.",
    "██▒▒▒▒▒▒▒▒ 20%": "A simple left-aligned loading bar showing 20 percent progress.",
    "███▒▒▒▒▒▒▒ 30%": "Sparse loading bar with a retro terminal feel.",
    "████▒▒▒▒▒▒ 40%": "A striped progress bar at forty percent.",
    "█████▒▒▒▒▒ 50%": "A loading bar for personal bios and status messages.",
    "██████▒▒▒▒ 60%": "Half-filled bar with crisp progress marker, busy yet clear.",
    "███████▒▒▒ 70%": "A half-filled progress bar with a percentage, suggesting loading status.",
    "████████▒▒ 80%": "A nearly complete progress bar showing 80 percent.",
    "█████████▒ 90%": "Clean horizontal bar showing near-complete progress.",
    "██████████ 100%": "A solid full bar with a completion percentage.",
    "[■□□□□□□□□□] 10%": "Loading bar for status or bio progress.",
    "[■■■□□□□□□□] 30%": "A half-filled progress bar with a clean 30 percent marker.",
    "[■■■■■□□□□□] 50%": "A half-filled progress bar showing fifty percent completion.",
    "[■■■■■■■□□□] 70%": "A near-complete progress bar with a numeric percentage.",
    "[■■■■■■■■■□] 90%": "Steady loading bar nearing full completion.",
    "[■■■■■■■■■■] 100%": "Alternating solid and empty blocks form a horizontal progress bar at full.",
    "▰▰▰▱▱▱▱▱▱▱ 30%": "Tall loading bar for a bio progress meter.",
    "▰▰▰▰▰▰▱▱▱▱ 60%": "Filled and empty blocks show a half-done loading bar.",
    "▱▰▰▰▰▰▰▰▰▰ 100%": "Solid bar at full, clean percentage readout.",
    "█░░░░░░░░░ 10%": "A thin, left-aligned bar showing a single filled cell at the start.",
    "█████░░░░░ 50%": "Steady half-filled bar with crisp geometric contrast.",
    "█████████░ 90%": "Spinning bar for a bio or status line.",
    "▒▒▒▒▒▒▒▒▒▒ 100% ᴄᴏᴍᴘʟᴇᴛᴇ": "A loading bar with a completed percentage for status updates or bios.",
    "██████████ ✓ ᴅᴏɴᴇ": "Steady filled bar with a crisp checkmark and compact label.",
    "◖■■■■■□□□□□◗ 50%": "Half-filled progress bar with a clean, balanced look.",
    "⟦████████░░⟧ 80%": "A progress bar with a filled and empty segment, showing a loading status.",
    "▰▰▰▰▰▰▰▰▰▰ ✓": "Solid checkmark atop a full progress bar signals completion."
  },
  "sparkles": {
    "✧･ﾟ: *✧･ﾟ:*": "A row of small stars and dots with a gentle, airy sparkle.",
    "*:･ﾟ✧*:･ﾟ✧": "A sparkling divider for brightening up titles.",
    "⋇⋆✦⋆⋇": "A symmetrical line of twinkling stars and diamonds.",
    "⭒❃.✮:▹": "A sharp arrow piercing a layered sparkle and star.",
    "‧͙⁺˚*･༓☾": "A centered, layered star framed by soft sparkles",
    ".｡.:*✧ ✧*:.｡.": "Delicate starbursts framed by tiny sparkles.",
    "✧༝┉˚*❋❤❋*˚┉༝✧": "A sparkling heart framed by delicate diamond stars and shimmering lines.",
    "✺✳ ┅ ⑅ ┅ ✳✺": "Metallic sparkle frame for a glowing name banner.",
    "✧༺♥༻∞": "A line of alternating hearts, sparkles, and infinity signs.",
    "*:..｡o○☆": "A cascading trail of stars and sparkles.",
    ".⋆｡⋆☂˚｡⋆｡˚☽˚｡⋆.": "A delicate umbrella framed by stars and a crescent moon.",
    "｡･:*:･ﾟ★,｡･:*:･ﾟ☆": "Delicate starbursts with a dreamy, twinkling glow.",
    "☆.｡.:* ☆○o": "Tiny star and dot sparkles in a gentle, twinkling row.",
    "【☆】★【☆】": "A sparkling star badge for a name or title.",
    ".・゜゜・ ・゜゜・．": "A soft, dotted shimmer that fades in and out.",
    "°。°。°。°。°。°。": "Tiny star-like dots in a gentle, floating line.",
    "✲꘏ ꘏ ꘏ ꘏✲": "A symmetrical burst framed by diamond-shaped sparkles.",
    "✼ ҉ ✼ ҉ ✼": "A delicate, shimmering line of celestial light.",
    "ﾟ+..｡*ﾟ+": "Dainty sparkle trail with a centered burst of light.",
    "⋆ ˚｡⋆୨୧˚": "Delicate sparkle frame perfect for a name or bio.",
    "⋆｡°✩ ✧": "A gentle shimmer of scattered stars and light.",
    "˚ ✦ ⭒ ˚ ✦ ⭒ ˚": "A merry-go-round of tiny stars and crescents.",
    "✦ ✧ ✦ ✧ ✦": "A symmetrical row of intertwined sparkles and twinkles.",
    "˚ ₊ ‧ ꒰ ✧ ꒱ ‧ ₊ ˚": "A soft, sparkly frame that feels gentle and whimsical.",
    "⊹ ˚ ✧ ˚ ⊹": "Delicate starbursts framed by floating sparkles.",
    "꒷ ✧ ꒦ ✧ ꒷": "A dainty sparkle border for names or titles.",
    "⋆ ˚ ✦ ˚ ⋆ ˚ ✦ ˚ ⋆": "Delicate spaced sparkles with a gentle twinkling rhythm.",
    "₊ ⊹ ✧ ⊹ ₊": "A symmetrical burst of sparkle with a diamond-shaped core."
  },
  "hanging-stars": {
    "＊*•̩̩͙✩•̩̩͙*˚": "A symmetrical star suspended within a softly glowing halo.",
    "˚*•̩̩͙✩•̩̩͙*˚＊": "Delicate hanging stars with a soft, dreamy charm.",
    "·͙*̩̩͙˚̩̥̩̥*̩̩̥͙ ✩ *̩̩̥͙˚̩̥̩̥*̩̩͙‧͙": "Delicate cascading stars with tiny trailing dots.",
    "-•̩̩͙-ˏˋ⋆ ⋆ˊˎ-•̩̩͙-": "Delicate hanging stars for a dreamy name or bio accent.",
    "·̩̩̥͙＊*•̩̩͙✩•̩̩͙*˚ ˚*•̩̩͙✩•̩̩͙*˚＊·̩̩̥͙": "Floating stars twinkle in a gentle, drifting line.",
    "-ˏˋ♥̩͙♥̩̩̥͙♥̩̥̩ ⑅⑅ ♥̩̥̩♥̩̩̥͙♥̩͙ˊˎ": "A soft cascade of tiny hearts and stars.",
    "༝̩̩̥͙ ༓༝̩̩̥͙ ⊹ ⊹༝̩̩̥͙ ༓༝̩̩̥͙": "Twin dangling star charms with gentle symmetry.",
    "⁎̩͙ ⁑̩͙̩͙ ⁂̩̩͙͙ ⁂̩̩͙͙ ⁑̩͙̩͙ ⁎̩͙": "A delicate garland of soft, twinkling star charms.",
    "﹥ˏˋ♡̩͙♡̩̩̥͙♡̩̥̩ ♡̩̥̩♡̩̩̥͙♡̩͙ˊˎ﹤": "Shooting-star charms with tiny hearts in a dreamy row.",
    "┉ˏ͛ ༝̩̩̥͙ ⑅͚˚ ҉ ⑅͚˚༝̩̩̥͙ˎ┉": "Tiny dangling stars for a dreamy bio header.",
    "ˏ⸉ˋ‿̩͙‿̩̩̥͙̽‿̩͙ ‿̩̥̩‿̩̩̥͙̽‿̩͙ˊ⸊ˎ": "Tiny stars drift in a gentle, airy line."
  },
  "waves": {
    ".·:*¨¨*:·.": "Flowing dot-and-swirl wave for soft rippling accents.",
    "•*´¨`*•.¸¸.•*´¨`*•.¸¸.•": "Flowing double row of tiny mirrored teardrops with soft dots.",
    "‿︵•‿︵•‿︵•‿︵": "Soft rippling wave accent for gentle separation.",
    "¸¸♫·¯·♪¸¸♩·¯·♬¸¸": "Flowing waves of music notes and dashes",
    "☆.。.:*・°☆.。.:*・°☆": "A gentle star-scattered ripple for a dreamy name divider.",
    "✯¸.•´*¨`*•✿ ✿•*`¨*`•.¸✯": "A delicate, symmetrical spray of tiny stars and flowers.",
    "¸,ø¤º°`°º¤ø,¸¸,ø¤º°°º¤ø,¸": "Flowing wave of curls and dots in a repeating ripple.",
    "°°°·.°·..·°¯°·._.··._.·°¯°·.·°": "A symmetrical ripple of small dots and curved lines, like gentle water rings.",
    ".•°°•..•°°•..•°°•.": "Gentle ripples like a calm sea breeze.",
    "∴.·:*¨¨*:·.": "Flowing wave of dots and stars bookended by peaks.",
    "❧.·:*¨ ¨*:·.∴": "A gentle swirl of dots and waves for a dreamy name divider.",
    "☙.·:*¨ ¨*:·.♡": "Soft, dotted wave with a tiny heart at center.",
    "⠂⠁⠈⠂⠄⠄⠂⠁⠁⠂⠄⠄⠂⠁⠁⠂": "A single rippling wave that gently undulates across the line.",
    "-⎽__⎽-⎻⎺⎺⎻-⎽__⎽--⎻⎺⎺⎻-": "Symmetrical peaks and valleys forming a gentle, rhythmic wave.",
    "⋅.˳˳.⋅ॱ˙˙ॱ⋅.˳˳.⋅": "Gentle ripples of soft motion, calm and serene.",
    "♪♫•*¨*•.¸¸❤¸¸.•*¨*•♫♪": "Rippling hearts framed by dancing music notes.",
    "彡彡◦༄◦°˚°◦.¸¸彡彡": "A gentle wave frame for a flowing name.",
    "‿‿‿‿‿‿‿‿": "Continuous wave ripples across the line.",
    "︵‿︵‿︵‿︵": "Flowing waves create a soft, rippling rhythm.",
    "～～～～～～～": "Flowing horizontal waves suggesting gentle motion.",
    ".·°°·..·°°·..·°°·.": "Gentle ripples in a calm, rhythmic dance.",
    "⌒⌒⌒⌒⌒⌒⌒": "Repeating soft arcs creating a gentle, rippling wave border.",
    ".·°•°·..·°•°·.": "A gentle ripple for a name divider or bio accent.",
    "≈≈≈≈≈≈≈≈": "Continuous wave texture with gentle rhythmic motion.",
    "〜〜〜〜〜": "Flowing wave line, gentle and continuous."
  },
  "music-players": {
    "► ▌▌ ■": "Equalizer bars and play controls in a clean row.",
    "↻ ◁ II ▷ ↺": "Retro player controls with a balanced, rhythmic symmetry.",
    "◁◁ ▐ ▌ ▷▷": "Twin double-arrows flanking a central pause bar.",
    "|◁ II ▷|": "Minimal playback controls for a music profile.",
    "↻ ◁ || ▷": "Rhythmic play controls with a repeating seek bar feel.",
    "◃◃ ⅠⅠ ▹▹": "Three continuous play controls in a tidy row.",
    "⇄ ◁ II ▷ ↻": "Symmetrical player controls with repeat and play symbols.",
    "ᴠᴏʟᴜᴍᴇ ▮▮▮▮▮▮▯▯▯": "Full volume bar glowing with a retro terminal vibe.",
    "■■■■■□□□": "Five solid and three empty blocks in a row.",
    "▁ ▂ ▃ ▄ ▅ ▆ █": "Equalizer bar for a music-themed profile.",
    "0:00 ───|────── 3:47": "A seek bar paused at the middle of a track.",
    "1:35 ───●───── 3:47": "Seek bar with progress dot near the start.",
    "↺ ᴿᴱᴾᴱᴬᵀ ‖ ᴾᴬᵁˢᴱ ≫ ᴺᴱˣᵀ": "Circular repeat, pause, and next symbols in a tidy row.",
    "▄ █ ▄ █ ▄ ▄ █ ▄ █ ▄ █": "Sparse equalizer bars with a glitchy, rhythmic pulse.",
    "◁ II ▷  ⠀ ──●───── ": "Equaliser bars flanked by play controls with a glowing seek dot.",
    "▶ ━━●─────── ⟲": "A sleek music player bar for a song title line."
  },
  "hearts": {
    "♡⸝⸝": "A gentle heartbeat of small hearts in a row.",
    "｡₊°༺❤︎༻°₊｡": "A layered heart framed in soft sparkles.",
    "♥••*´¨`*•.☆•": "A heart crowned by a delicate star.",
    "•❀.•❤•.¸✿¸.•❤•.❀•": "Delicate heart-and-flower border with soft romantic charm.",
    "↘❀↗↘❤↗↘❤↗↘❀↗": "A row of hearts framed by flowers, soft and romantic.",
    "｡･:*:･ﾟ❤,｡･:*:･ﾟ❤": "Delicate hearts linked by soft dots, perfect for a sweet signature.",
    "⟡ ♡ ⟡": "Three hearts framed by tiny stars, soft and sweet.",
    "꒰ ♡ ꒱": "Tiny heart nested in soft parentheses.",
    "𓆩♡𓆪": "A symmetrical heart framed by delicate floral wings.",
    "⋆｡°✩ ♡ ✩°｡⋆": "Soft pink hearts framed by tiny stars.",
    "❣ ⋆ ❣": "Dual hearts with a sparkling star between them.",
    "♡ ─── ♡ ─── ♡": "Tiny hearts spaced along a line, perfect for framing a sweet name.",
    "˚ʚ♡ɞ˚": "Delicate heart wrapped in soft floating wings.",
    "⊹ ♡ ⊹ ♡ ⊹": "A delicate chain of tiny hearts and floral dots, soft and romantic.",
    "♥̩͙ ♥̩̩̥͙ ♥̩̥̩": "Three layered hearts in a soft, symmetrical stack.",
    "·͜· ♡": "Tiny heart centered in soft, gentle dots.",
    "ღ♡ღ♡ღ♡ღ♡ღ": "A row of tiny sparkling hearts strung together.",
    "˚｡❤｡˚ ⋆ ˚｡❤｡˚": "Delicate hearts cradled in stars, sweet for a name.",
    "♡｡˚♡｡˚♡｡˚♡｡˚♡": "Tiny hearts drift in a soft, dotted wave."
  },
  "dots": {
    "••●••": "Five dots with a dark bead center.",
    "─┉┈◈◉◈┈┉": "Symmetrical diamond bead with dotted line arms.",
    ".·:*¨༺༻¨*:·.": "Delicate bead-like border with soft, romantic symmetry.",
    "•,¸,.·''·.,¸,•": "A dotted, beaded line with gentle curves for a soft section break.",
    "︑︒⚬∙︓·⠄": "Fine beaded line for a delicate name divider.",
    "»--•--«": "Beaded line with gentle arrow-like ends.",
    ".⋅ ۵♡۵ ⋅.": "Heart bead with diamond dots",
    "●・○・●・○・●": "A balanced dot-and-bead line for clean section breaks.",
    "●～●～●～●～●": "Beaded dotted line for gentle, tidy breaks.",
    "●❍°•°•°○°•°•°❍●": "A centered bead framed by a mirrored dot-and-ring pattern.",
    "×•-•-•-• ◆ •-•-•-•×": "A beaded line with a diamond center, perfect for name dividers.",
    "°•°•°•°•°•°•°": "A dotted line with gentle, patterned rhythm.",
    "★・・・・・・★": "Two star ends on a dotted line.",
    "*+:｡.｡｡.｡:+*": "Symmetrical dotted beads framing a delicate center space.",
    "·•· ✦ ·•·": "Tiny stars framed by beads, soft and whimsical.",
    "⋆˚｡◦｡˚⋆◦⋆˚｡◦｡˚⋆": "Repeating star-and-dot beads in a soft, symmetrical string.",
    "∙∘○◦•◦○∘∙∘○◦•◦○∘∙": "Elegant beaded line for name or bio breaks.",
    "·•⋅◦⋅•·◦·•⋅◦⋅•·": "A gentle dotted line alternating with tiny beads, like soft footsteps.",
    "◌•◍•◌•◍•◌•◍•◌": "Beaded dot chain with alternating filled and open circles."
  },
  "arrows": {
    "»»——⍟——««": "Double-sided arrow with central star balanced on a line.",
    "➴➵➶➴➵➶➴➵➶": "Arrows swooping down in a playful, rhythmic zigzag.",
    "◃───────────▹": "Repeating triangles and dashes form a balanced horizontal pointer.",
    "»»-----------►": "Bold arrow pointer for section titles and bio headers.",
    "╰┈➤": "A sharp little arrow and dotted curve, like a sleek text pointer.",
    "↶*ೃ✧˚. ❃ ↷ ˊ-": "Dainty arrow curving through a star and bloom",
    "﹀﹀︵↷": "Swooping downward arrows with a curved twist.",
    "❍⌇─➭": "Sleek pointing arrow with a subtle dash and dot lead.",
    "»»ᅳᅳᅳᅳ►": "Flowing arrow pointers guide the eye rightward.",
    "⇘⇙ ⇗⇖": "Flanking arrows guiding the eye on either side of a name.",
    "↳ ‧₊˚✧": "Flowing arrow with starburst and soft dots.",
    "→→→ ⛤ ←←←": "Star-like point framed by left and right arrows.",
    "·˚ ➶ ｡˚": "Airy arrows drift apart like scattered dust motes.",
    "⋆˚࿔ ⟶⟶⟶ ࿔˚⋆": "Flowing arrows framed by delicate stars and soft curves.",
    "·•➜·•➜·•➜·•➜·•": "A row of angled arrows flowing rightward, guiding the eye along a path.",
    "↫↬↫ ✧ ↬↫↬": "Flowing arrows framing a spark, perfect for a signature line.",
    "⟿⋆⟿⋆⟿⋆⟿⋆⟿": "Alternating arrows and stars create a brisk, dotted trail."
  },
  "flowers": {
    "୨⎯ 🌹 ⎯୧": "A single rose framed by delicate curved darts.",
    "ʚ🌹ɞ": "Delicate rose framed by mirrored swirls.",
    "•┈••✦ 🌹 ✦••┈•": "A rose framed by delicate, symmetrical dotted vines.",
    "◦◦,`°.✽✦✽.◦.✽✦✽.°`,◦◦": "Delicate star blossoms framed by soft dotted arcs.",
    "❀ ✿ ❁ ✾ ❀": "Whimsical floral divider for a dreamy spring profile name.",
    "⊰ ✿ ⊱": "Tight floral cluster with soft, pointed petals.",
    "✤ ❀ ✤ ❀ ✤": "A leafy blossom framed by symmetrical floral crosses.",
    "✿ ˚ ❀ ˚ ✿": "A symmetrical floral trio with soft, airy blooms.",
    "⋆ ❀ ⋆ ❀ ⋆": "Delicate star-flower chain, airy and sweet.",
    "✎ (❁ᴗ͈ˬᴗ͈) ༉‧ ♡*.✧": "A curled leaf topped with a tiny blossom and a sparkle.",
    "❀° ⋅ ❀ ⋅ °❀": "Delicate flower trio for a soft signature line",
    "✿ ⋆ ❀ ⋆ ✿": "Balanced blooms with soft star accents.",
    "✽ ⋆｡°✩ ✽": "A symmetric blossom with a star-like center and soft sparkles.",
    "❁ ⋆ ❁ ⋆ ❁": "Delicate starred blossoms in a balanced horizontal row.",
    "✾ ｡˚ ✿ ˚｡ ✾": "Delicate blossoms floating in soft, airy symmetry.",
    "⊰ ❀ ─── ❀ ⊱": "Delicate blossoms linked by gentle lines, like a floral garland."
  },
  "circles": {
    "●○●○●○●○●": "A rhythmic circle-and-bead divider for header or bio.",
    "★○★○★○★": "Repeating stars and circles in a steady, playful beat.",
    "◇──◆──◇──◆": "A chain of diamonds with alternating dark and light beads.",
    "◈━◈━◈━◈━◈": "Three diamond beads threaded on a straight line.",
    "◈◆◈◆◈◆◈◆◈": "Alternating diamonds and dots in a circular chain.",
    "━◦○◦━◦○◦━": "Circular bead motif alternating with dashes in a rhythmic row.",
    "✧○ꊞ○ꊞ○ꊞ○ꊞ○✧": "Small circle beads between star accents, perfect for a minimalist name divider.",
    "◇●○◆○●◇●○◆○●◇": "Beaded circles alternating with diamonds in a rolling rhythm.",
    "∘°∘♡∘°∘": "Delicate heart cradled by shimmering dotted circles.",
    "◦•●◉✿✿◉●•◦": "Symmetrical floral beads circle a dotted center.",
    "●∘◦❀◦∘●": "Playful floral bead circling a central dot.",
    "──⇌••⇋──": "Balanced bead-and-arrow rhythm for round, flowing dividers.",
    "≪ ◦ ❖ ◦ ≫": "A circular frame of small dots and diamonds for an elegant nameplate.",
    "──●◎●──": "A rhythmic line of small orbs and rings.",
    "━ ◦ ❖ ◦━": "Central diamond framed by circles and lines.",
    "○ ◯ ⊙ ◯ ○": "Concentric circles with a glowing centre, rhythmic and balanced.",
    "◍◌◍◌◍◌◍◌◍◌◍": "Alternating filled and hollow circles in a calm rhythmic line.",
    "⊙⊚⊙⊚⊙⊚⊙⊚⊙⊚⊙": "Alternating circles and dotted rings form a rhythmic circular divider.",
    "◯●◦●◯●◦●◯●◦●◯": "Alternating circles as a rhythmic name divider."
  },
  "squares": {
    "◈▢◈▢◈▢◈▢◈": "Alternating diamond and square tiles create a crisp, rhythmic row.",
    "▣▣▣▣▣▣▣▣": "Four black squares in a tight row.",
    "◆:*:◇:*:◆:*:◇:*:◆": "A balanced diamond-star sequence with alternating shaded and open diamonds.",
    "▰▰▰▰▰▰▰▰▰▰▰▰": "Solid block row, clean and modern break.",
    "◇◆◇◆◇◆◇◆◇": "Chessboard diamond checkerboard border.",
    "■□■□■□■□■□■□■": "Checkerboard band for a sleek profile divider.",
    "◈◈◈▣▣◈◈◈▣▣◈◈◈": "Alternating diamonds and blocks in a steady, rhythmic pattern.",
    "▄▀▄▀▄▀▄▀▄▀▄▀▄": "Alternating solid and outlined squares in a checkerboard stripe.",
    "▂ ▃ ▄ ▅ ▆ ▇ █ ▇ ▆ ▅ ▄ ▃ ▂": "Thick striped bars forming a symmetrical diamond peak.",
    "▰▱▰▱▰▱▰▱▰▱▰▱": "Alternating solid and empty blocks forming a rhythmic checkerboard line.",
    "┴┈┈■┈┈┴": "A centered black tile framed by dashes and T-shapes.",
    "┬┈┈□┈┈┬": "A crisp title frame with raised corners.",
    "▬▭▬ ✦ ▬▭▬": "Sharp blocks framing a tiny star, crisp and balanced.",
    "◧ ◨ ◧ ◨ ◧": "Alternating squares create a crisp geometric rhythm.",
    "❑ ❒ ❑ ❒ ❑": "Alternating solid and open squares form a crisp, balanced row.",
    "▦▧▦▧▦▧▦": "Bold alternating squares forming a tight grid pattern.",
    "▪▫▪▫▪▫▪▫▪▫▪": "Alternating solid and empty small squares forming a crisp, checkerboard stripe.",
    "◰◱◲◳◰◱◲◳◰◱◲◳": "Geometric blocks that frame a bold, modern signature line.",
    "▩▦▩▦▩▦▩▦▩▦▩": "Alternating solid and outlined squares in a steady march."
  },
  "triangles": {
    "━━▲━━": "Sharp triangle framed by parallel horizontal bars.",
    "▔▔▿▔▔": "Bold arrowhead framed by two horizontal lines.",
    "◤◢◣◥◤◢◣◥◤◢◣◥": "Bold, repeating zigzag of sharp arrowheads.",
    "❢◥ ▬▬▬ ◆ ▬▬▬ ◤❢": "Sharp arrowheads flank a central diamond, balanced and directional.",
    "◥✥◤ ◢✥◣": "Edgy diamond frames for name headers",
    "◄░░░░░░►": "Sharply pointed arrowhead bookended by dotted trail lines.",
    "° ∆ ------ ••• ----- ∆ °": "Sharp arrowheads bookend a dotted line.",
    "∆∇∆∇∆∇∆∇∆∇∆∇": "Alternating peak and valley triangles in a zigzag band.",
    "▲▼▲▼▲▼▲": "Sharp alternating arrowheads in a bold, rhythmic zigzag.",
    "△▽△▽△▽△": "Sharp zigzag of alternating triangles.",
    "▷◁▷◁▷◁": "Sharp arrowed triangles for edgy name dividers.",
    "◤━━━━━◥\n◣━━━━━◢": "Sharp interlocking arrow bands framing a solid border.",
    "◬ ◭ ◮ ◭ ◬": "A balanced row of upright and inverted triangles.",
    "▴ ▵ ▴ ▵ ▴": "Alternating small and large triangles form a rhythmic arrowhead band.",
    "▲△▲△▲△▲△▲△▲": "Sharp zigzag line of alternating arrows",
    "◀▶◀▶◀▶◀▶◀▶◀": "Alternating left-right arrows in a tight, sharp repeating row.",
    "⊳⊲⊳⊲⊳⊲⊳⊲⊳⊲⊳": "Repeating arrowheads form a sharp, directional border for names."
  },
  "music": {
    "♬ ♪ ♫ ♩": "A flowing line of notes and rests with a gentle, melodic rhythm.",
    "♩ ♪ ♫ ♬ ♭ ♮ ♯": "Musical staff line with gentle note progression.",
    "🎵 ⋆｡°✩ 🎶": "Two musical notes framed by a starry, symmetrical burst.",
    "♪♫•*¨*•♫♪": "A soft, symmetrical flourish of notes framed by tiny stars.",
    "🎧 ♫ ┈┈┈ ♫ 🎧": "Headphones framing a row of musical notes",
    ".ılılıll 🎵 llılılı.": "Flowing notes frame a song title or artist name.",
    "♪ ┈ ♫ ┈ ♪ ┈ ♫": "Alternating notes float in a spaced, dancing rhythm.",
    "🎶 ⋆ ♪ ⋆ 🎶": "A symmetric spread of musical notes and stars.",
    "🎸 🎻 🎹 🎺 🎷": "A band of instruments arranged in a balanced row.",
    "🎵 ━━━━ ♫ ━━━━ 🎵": "A symmetrical border of music notes for a melodic, rhythmic accent.",
    "˚｡⋆ ♫ ⋆｡˚": "A curling melody of stars and a single musical note.",
    "♪ ⋆ ｡ ♬ ｡ ⋆ ♪": "A musical name or bio border with notes and stars.",
    "▁▂▃▄▅▆▇ ♪ ▇▆▅▄▃▂▁": "A mirrored wave of sound, rising and falling around a single note.",
    "♡ ´¨`•.¸¸.♫ ♫.¸¸.•´¨` ♡": "A heart framed by floating eighth notes.",
    "⋆ ✦ ♪ ✦ ⋆": "A symmetrical star and music note accent.",
    "♫ ♬ ♫ ♬ ♫": "Flowing notes in a playful, rhythmic loop."
  },
  "aesthetic": {
    ": ̗̀➛": "A thin right arrow preceded by a floating colon.",
    "⊹ ࣪ ˖": "Tiny sparkle accent for names and bios.",
    "౨ৎ˚₊": "Delicate flourishes with a soft, flowing rhythm.",
    "𝜗𝜚⋆₊˚": "A delicate looped flourish with a soft sparkle.",
    "˙⋆✮": "Tiny star with a trailing spark, delicate.",
    "ੈ✩‧₊˚": "Soft dreamy stars wrapped in gentle sparkles.",
    "⋆·˚ ༘ *": "Delicate star and dot pattern with a light, airy feel.",
    "ˏˋ°•*⁀➷": "A dainty arrow flourish for a name or title.",
    "·˚ ༘ ⋆.ೃ࿔*:･": "Soft dots and stars in a drifting, delicate line.",
    "˚₊· ͟͟͞͞➳❥": "Delicate arrow with a tiny heart at the tip.",
    "⋆.ೃ࿔*:･": "A delicate, star-topped vertical flourish with a soft trailing tail.",
    "✧˖°.": "Delicate star and dot flourish with a soft celestial feel.",
    "⋆˚࿔": "A tiny star with a trailing wispy flourish.",
    "⋆⭒˚.⋆": "A dainty starry border for a soft profile name.",
    "˚ ✦ ⋆｡°✩": "Delicate twinkling star dots drifting in a soft, airy line.",
    "♡⸝⸝": "A tiny heart flanked by delicate curled flourishes.",
    "꒰ ♡ ꒱ ⋆": "A tiny heart cradled between paired brackets with a star beside it.",
    "✩₊˚.⋆☾⋆⁺₊✧": "Soft celestial stars and crescent moon in layered stardust.",
    "⋆｡‧˚ʚ♡ɞ˚‧｡⋆": "A ring of stars framing a tiny heart.",
    "₊˚⊹♡": "Dainty heart with sparkles for a soft bio accent.",
    "˗ˏˋ ★ ˎˊ˗": "A delicate star framed by soft, mirrored wings.",
    "˚｡⋆୨୧˚": "Tiny ribbon bow nestled within soft sparkles.",
    "⊹ ࣪ ˖ ⟡": "Three tiny symmetrical dots in a line.",
    "✿ ˚. ᵎᵎ": "Delicate floral flourish with soft sparkles and gentle energy.",
    "❀⋆.ೃ࿔*:･": "A delicate floral and star flourish with a soft, whimsical vibe.",
    "⋆˙⟡♡": "A tiny floral heart, perfect for a soft bio accent.",
    "₊˚ʚ ᗢ ₊˚✧": "A soft, cat-like flourish with a sparkle on each side.",
    "✦ ˖° ⋆": "A delicate star with a tiny dot and sparkle beside it.",
    "꒰ ๑ ꒱": "A symmetrical flourish with a centred dot, soft and minimal.",
    "⋆ ˚｡⋆୨♡୧⋆ ˚｡⋆": "Soft pink heart framed by tiny stars and sparkles",
    "⊹ ★ ⊹ ★ ⊹": "A symmetrical cluster of stars and sparkles with a delicate, magical feel.",
    "✩°｡⋆⸜ ⋆": "A delicate starry flourish for a name or bio header.",
    "⋆.˚🦋༘⋆": "A delicate butterfly floats along a dotted trail of stars.",
    "♡ ₊˚ ⟢ ˚₊ ♡": "A dainty heart framed by soft, star-like flourishes.",
    "⟢ ₊˚ ♡ ˚₊ ⟢": "A symmetrical heart framed by delicate, starry flourishes.",
    "⋆ ｡˚⟡˚｡ ⋆": "A soft, twinkling star framed by delicate dots.",
    "˚₊‧ ⋆｡☾｡⋆ ‧₊˚": "A crescent moon nestled within layered starry halos.",
    "˖⁺‧₊˚ ♡ ˚₊‧⁺˖": "Gently wrapped hearts, perfect for softening a name or title.",
    "⋆｡˚ ☾ ˚｡⋆": "Slender crescent cradled in a soft starry shimmer.",
    "⊹ ࣪ ˖ ♡ ˖ ࣪ ⊹": "Delicate heart framed by soft dotted flourishes.",
    "⟡ ⋆ ⟡ ⋆ ⟡": "Symmetrical star-and-diamond flourish pattern.",
    "✦ ⋆ ｡ ˚ ✦": "Dreamy starbursts in a soft, floating trail."
  },
  "ornate-frames": {
    "╔═━─━─≪ ✦ ≫─━─━═╗\n╚═━─━─≪ ✦ ≫─━─━═╝": "Symmetrical diamond framed by mirrored wings.",
    "☆.｡.:*・°★°・*:.｡.☆\n☆.｡.:*・°★°・*:.｡.☆": "Symmetrical twin-star frame with soft dotted borders.",
    "┏━◈━━━━━━━━━◈━┓\n┗━◈━━━━━━━━━◈━┛": "Opulent diamond-studded border for regal titles.",
    "✦•◦•─────────•◦•✦\n✦•◦•─────────•◦•✦": "Small star-and-dot border framing a bold horizontal line.",
    "❁ ✿ ❀ ───────── ❀ ✿ ❁\n❁ ✿ ❀ ───────── ❀ ✿ ❁": "Delicate floral bookend frames for name or title.",
    "╭─♡─────────♡─╮\n╰─♡─────────♡─╯": "A soft, heart-anchored frame with rhythmic, gentle corners.",
    "❖═══════════════❖\n❖═══════════════❖": "Bold diamond-shaped frame flanked by symmetrical lines.",
    "⋆⭒˚｡⋆─────────⋆｡˚⭒⋆\n⋆⭒˚｡⋆─────────⋆｡˚⭒⋆": "Delicate symmetric frame with soft star-like accents at each end.",
    "♕ ･ﾟ✧ ───────── ✧ﾟ･ ♕\n♕ ･ﾟ✧ ───────── ✧ﾟ･ ♕": "Regal crown motif framing a name in dreamy starry lines.",
    "༺❀༻────────────༺❀༻\n༺❀༻────────────༺❀༻": "Delicate flower motifs bookend a symmetrical twin-bordered frame.",
    "⊰ ⋆ ⊱━━━━━━━━━⊰ ⋆ ⊱\n⊰ ⋆ ⊱━━━━━━━━━⊰ ⋆ ⊱": "Elaborate twin frames with star-and-arrow ends for a regal title.",
    "◜◝ ✦ ◜◝ ✦ ◜◝ ✦ ◜◝\n◟◞ ✧ ◟◞ ✧ ◟◞ ✧ ◟◞": "A delicate sequence of paired arcs and tiny stars.",
    "❰═══◆═══════◆═══❱\n❰═══◆═══════◆═══❱": "Bold double frame with diamond anchors.",
    "✿｡‧˚────────˚‧｡✿\n✿｡‧˚────────˚‧｡✿": "Delicate floral brackets framing a centered space.",
    "┌─◌─◍─◌─◍─◌─┐\n└─◌─◍─◌─◍─◌─┘": "Dual-toned frame with soft, elegant rhythm for formal titles.",
    "≪ °✦° ≫━━━━━━≪ °✦° ≫\n≪ °✦° ≫━━━━━━≪ °✦° ≫": "Double star-dusted dividers framing a symmetrical two-line border.",
    "╓──♔──────────♔──╖\n╙──♔──────────♔──╜": "Regal crowned border for a name or title.",
    "⊹⊱✿⊰⊹──────⊹⊱✿⊰⊹\n⊹⊱✿⊰⊹──────⊹⊱✿⊰⊹": "A symmetrical frame of tiny flowers and dashes.",
    "╔══✦══✿══✦══╗\n╚══✦══✿══✦══╝": "Delicate flowers and diamonds frame a symmetrical title.",
    "◦°˚✧──────────✧˚°◦\n◦°˚✧──────────✧˚°◦": "A symmetrical double border with diamond-like center points.",
    "❃ ⊹ ❃ ─────────── ❃ ⊹ ❃\n❃ ⊹ ❃ ─────────── ❃ ⊹ ❃": "Delicate floral bookend borders for a romantic heading.",
    "┎┈┈❀┈┈┈┈┈┈❀┈┈┒\n┖┈┈❀┈┈┈┈┈┈❀┈┈┚": "A floral-dotted double frame with small blossoms at each side."
  },
  "celestial": {
    "₊˚⊹♡⋆｡˚꒰ ⋆ ꒱˚｡⋆♡⊹˚₊": "A layered star and moon motif wrapped in soft, dreamy symmetry.",
    "⋆˚࿔ ⊹ ˚｡⋆ ✧ ⋆｡˚ ⊹ ࿔˚⋆": "Concentric rings of stars and moons, softly glowing with layered symmetry.",
    "˚ ༘ ⋆｡˚ ☾ ˚｡⋆ ༘ ˚": "layered crescent and star wisp for a dreamy bio header.",
    "⊹ ࣪ ˖ ⋆｡°✩ ✩°｡⋆ ˖ ࣪ ⊹": "Interlocking stars in a glowing, symmetrical celestial band.",
    "˚₊‧꒰ ☆ ꒱‧₊˚": "Dreamy star cradled in soft, layered glow.",
    "✶⋆.˚ ⊹ ♡ ⊹ ˚.⋆✶": "Layered hearts and stars in a soft, floating rhythm.",
    "❀°•°❀ ⊰ ✿ ⊱ ❀°•°❀": "Concentric blossoms ringed by stellar points.",
    "·˚ ༘ ₊˚.⋆☾⋆⁺₊✧ ⋆.˚ ༘ ·": "A symmetrical, layered crescent cradling a single star, soft and dreamy.",
    "⟡ ⋆｡˚ ✦ ˚｡⋆ ⟡": "Layered lunar and star shapes in a soft, dreamy sequence",
    "˗ˏˋ ✦ ⋆ ✿ ⋆ ✦ ˎˊ˗": "A symmetrical wreath of stars and flowers",
    "｡ﾟ•┈୨♡୧┈•ﾟ｡": "Layered moon-and-star divider for a dreamy bio name.",
    "✩₊˚.⋆☾⋆⁺₊ ✧ ₊⁺⋆☽⋆.˚₊✩": "A shimmering crescent framed by tiny stars.",
    "⋆｡°✩₊˚ ⊹ ☾ ⊹ ˚₊✩°｡⋆": "Layered moon and stars framing a dreamy bio name.",
    "˚ ⊹ ࣪ ✧ ⋆｡˚ ♡ ˚｡⋆ ✧ ࣪ ⊹ ˚": "Layered crescent and star rhythm with a soft, dreamy pulse.",
    "⋅˚₊‧ ୨୧ ‧₊˚⋅ ✩ ⋅˚₊‧ ୨୧ ‧₊˚⋅": "Delicate star cradled between mirrored crescent arches.",
    "｡ﾟ☆: *.☽.* :☆ﾟ｡": "Layered rings of stars framing a central crescent moon.",
    "·｡˚ ✧ ☾ ✦ ☽ ✧ ˚｡·": "Soft layered moons and stars with a dreamy glow.",
    "⋆˙⟡ ⊹ ࣪ ˖ ⋆ ♡ ⋆ ˖ ࣪ ⊹ ⟡˙⋆": "Layered star and heart flourishes for a dreamy celestial border.",
    "✶ ₊˚ ⊹ ⋆ ✧ ⋆ ⊹ ˚₊ ✶": "A layered celestial ornament for a mystical bio header.",
    "˚ ✧ ｡⋆ ࿐ ⋆｡ ✧ ˚": "A soft pulse of stars and crescent shapes.",
    "⊹₊ ⋆ ⌒⊹⌒ ⋆ ₊⊹": "Layered crescent crowned by a single star.",
    "·˚₊‧⁺˖ ⋆☾✦☽⋆ ˖⁺‧₊˚·": "Concentric rings of starlight framing a crescent core."
  },
  "art-deco": {
    "▰▱▰▱▰▱◆▱▰▱▰▱▰\n▰▱▰▱▰▱◆▱▰▱▰▱▰": "Bold art-deco geometry with diamond accents.",
    "◣◢◣◢◣◢◣◢◣◢◣◢\n◥◤◥◤◥◤◥◤◥◤◥◤": "Alternating chevron bands forming a dense art-deco border.",
    "║═══════◈═══════║\n║═══════◈═══════║": "Bold art-deco frame with diamond center for a name or title.",
    "◇◆◇◆◇◆◇◆◇◆◇\n◇◆◇◆◇◆◇◆◇◆◇": "Dense diamond grid with a sleek, geometric rhythm.",
    "≫═══════◆═══════≪\n≫═══════◆═══════≪": "Bold diamond framed by mirrored arrow lines.",
    "╳═╳═╳═◆═╳═╳═╳\n╳═╳═╳═◆═╳═╳═╳": "Symmetrical art-deco frame with bold diamond center.",
    "▲▽▲▽▲▽▲▽▲▽▲▽\n▽▲▽▲▽▲▽▲▽▲▽▲": "Bold zigzag frame with a rhythmic, art-deco rhythm.",
    "┃▰┃▰┃▰┃▰┃▰┃▰┃\n┃▰┃▰┃▰┃▰┃▰┃▰┃": "Bold block-and-diamond frame repeats in rigid columns.",
    "◢◤◢◤◢◤◆◥◣◥◣◥◣\n◢◤◢◤◢◤◆◥◣◥◣◥◣": "A bold art-deco border for names or titles.",
    "╬═══════◇═══════╬\n╬═══════◇═══════╬": "Bold block-and-diamond frame with mirrored vertical rhythm."
  },
  "botanical": {
    "❀✾❀──────────❀✾❀\n❀✾❀──────────❀✾❀": "Twin floral medallions linked by a gentle botanical dash.",
    "❦ ❧ ────────── ❧ ❦\n❦ ❧ ────────── ❧ ❦": "Mirrored leafed vine border with balanced floral ends.",
    "✿ ⊱ ❀ ⊰ ✿ ────── ✿ ⊱ ❀ ⊰ ✿\n✿ ⊱ ❀ ⊰ ✿ ────── ✿ ⊱ ❀ ⊰ ✿": "Delicate floral bookends framing a soft garden divider.",
    "⊰❀⊱┄┄┄┄┄┄┄┄⊰❀⊱\n⊰❀⊱┄┄┄┄┄┄┄┄⊰❀⊱": "A row of delicate flower-and-vine motifs framed by dots.",
    "✽ ❀ ✾ ─────────── ✾ ❀ ✽\n✽ ❀ ✾ ─────────── ✾ ❀ ✽": "Delicate flower-and-vine border for a soft bio or title frame.",
    "❁❃❋──────────❋❃❁\n❁❃❋──────────❋❃❁": "A mirrored vine border with floral endpoints and a dashed center.",
    "☘ ✿ ☘ ────────── ☘ ✿ ☘\n☘ ✿ ☘ ────────── ☘ ✿ ☘": "Delicate clover-and-flower border with balanced, symmetrical charm.",
    "⟢❀⟣──────────⟢❀⟣\n⟢❀⟣──────────⟢❀⟣": "Symmetrical vine border with a pair of delicate flowers.",
    "❀ ࿔ ✿ ────────── ✿ ࿔ ❀\n❀ ࿔ ✿ ────────── ✿ ࿔ ❀": "Delicate flowering vines framing a soft, romantic border.",
    "✿•❀•✾──────✾•❀•✿\n✿•❀•✾──────✾•❀•✿": "Flower-tipped vines frame a soft, symmetrical border."
  },
  "kawaii": {
    "˚ʚ♡ɞ˚────────˚ʚ♡ɞ˚\n˚ʚ♡ɞ˚────────˚ʚ♡ɞ˚": "A soft double frame lined with tiny hearts, perfect for a sweet name or title.",
    "ʚ♡ɞ─────────ʚ♡ɞ\nʚ♡ɞ─────────ʚ♡ɞ": "Soft heart borders repeated in gentle rows.",
    "꒰ ⑅ ꒱ ───────── ꒰ ⑅ ꒱\n꒰ ⑅ ꒱ ───────── ꒰ ⑅ ꒱": "Delicate frame of bows with soft, cheerful dots.",
    "♡｡ﾟ────────────ﾟ｡♡\n♡｡ﾟ────────────ﾟ｡♡": "A soft double frame dotted with tiny hearts.",
    "⑅ ♡ ⑅ ───────── ⑅ ♡ ⑅\n⑅ ♡ ⑅ ───────── ⑅ ♡ ⑅": "Soft heart-and-bow dividers with a sweet, pastel feel.",
    "✿ ♡ ✿ ─────────── ✿ ♡ ✿\n✿ ♡ ✿ ─────────── ✿ ♡ ✿": "A soft border of hearts and flowers.",
    "♡ ⊹ ♡ ⊹ ♡──────♡ ⊹ ♡ ⊹ ♡\n♡ ⊹ ♡ ⊹ ♡──────♡ ⊹ ♡ ⊹ ♡": "A soft, heart-dotted frame for a sweet name.",
    "˚ ✩ ♡ ✩ ˚ ──────── ˚ ✩ ♡ ✩ ˚\n˚ ✩ ♡ ✩ ˚ ──────── ˚ ✩ ♡ ✩ ˚": "Soft repeating hearts and stars create a gentle, playful border rhythm.",
    "ɞ˚୨୧˚ɞ──────ɞ˚୨୧˚ɞ\nɞ˚୨୧˚ɞ──────ɞ˚୨୧˚ɞ": "Soft heart-winged frames with gentle symmetry.",
    "(♡)─────────(♡)\n(♡)─────────(♡)": "Two hearts frame a soft, symmetrical border."
  },
  "gothic": {
    "┣━━━━━━✠━━━━━━┫\n┣━━━━━━✠━━━━━━┫": "Dark cruciform frames with heavy, cloistered symmetry.",
    "▓▒░──────────░▒▓\n▓▒░──────────░▒▓": "Dark cross-shaped frame with shaded vertical borders.",
    "╪═══════†═══════╪\n╪═══════†═══════╪": "Heavy gothic frame with crosses for dark titles or bios.",
    "☩ ━━━━━━━━━━━━ ☩\n☩ ━━━━━━━━━━━━ ☩": "Heavy cross-ended double bar with dark symmetry.",
    "╬══════♰══════╬\n╬══════♰══════╬": "Heavy cross framed by dark symmetry.",
    "┏━┅━┅━†━┅━┅━┓\n┗━┅━┅━†━┅━┅━┛": "Heavy dark frame with centered crosses and balanced symmetry.",
    "⊹ ✠ ⊹ ━━━━━━━━ ⊹ ✠ ⊹\n⊹ ✠ ⊹ ━━━━━━━━ ⊹ ✠ ⊹": "Dark crosses framed by heavy lines, solemn and medieval.",
    "▌▐▌▐▌▐◆▌▐▌▐▌▐\n▌▐▌▐▌▐◆▌▐▌▐▌▐": "Dark cross-shaped medallions in a dense repeating grid.",
    "╾━╾━╾━◆━╼━╼━╼\n╾━╾━╾━◆━╼━╼━╼": "Dark cruciform frame, heavy symmetry for a gothic title.",
    "▚▚▚▚▚▚◆▚▚▚▚▚▚\n▞▞▞▞▞▞◆▞▞▞▞▞▞": "Dark diamond-studded bar dividers with a repeating sawtooth rhythm."
  },
  "banners": {
    "⊰•◦❥◦•⊱": "A heart cradled by sweeping leaves, soft and romantic.",
    "❰━━━━━ ✦ ━━━━━❱": "A centered star framed by balanced horizontal bands.",
    "⊱ ─────── ❈ ─────── ⊰": "Delicate star centered on a symmetrical ribbon line.",
    "◤━━━━━ ✿ ━━━━━◢": "A thick border of lines ending in corner arrows cradles a tiny flower.",
    "≫───────♛───────≪": "Regal banner framing a single name or title.",
    "❮━━━━━ ❖ ━━━━━❯": "Smooth horizontal flow with a central diamond accent.",
    "⊰ ⋅•⋅ ⊰ ✦ ⊱ ⋅•⋅ ⊱": "A diamond wrapped in delicate ribbon flourishes",
    "⊱✦⊰━━━━━━━━⊱✦⊰": "Symmetrical ribbon with star-shaped pins at each end.",
    "►─────── ◆ ───────◄": "A sleek, centered diamond banner framed by arrowed lines.",
    "◥◤ ─────── ✧ ─────── ◢◣": "Symmetrical ribbon ends flanking a central star on a long line."
  },
  "corners": {
    "┏━━━━━ ✦ ━━━━━┓\n┗━━━━━ ✦ ━━━━━┛": "Ornate star-cornered frame for a name or title.",
    "╭━━━━━ ✿ ━━━━━╮\n╰━━━━━ ✿ ━━━━━╯": "Bold horizontal lines with floral accents at the corners.",
    "⌜━━━━━ ♡ ━━━━━⌝\n⌞━━━━━ ♡ ━━━━━⌟": "A heart centered between mirrored corner brackets.",
    "┎━━━━━ ❖ ━━━━━┒\n┖━━━━━ ❖ ━━━━━┚": "Symmetrical corner frame with diamond center motif.",
    "▗▄▄▄▄ ★ ▄▄▄▄▖\n▝▀▀▀▀ ★ ▀▀▀▀▘": "A star centered in a delicate open frame.",
    "╒═════ ◆ ═════╕\n╘═════ ◆ ═════╛": "A diamond-centered frame with symmetrical top and bottom border lines.",
    "┍━━━━━ ✧ ━━━━━┑\n┕━━━━━ ✧ ━━━━━┙": "Angled brackets with a star center, perfect for a nameplate.",
    "╓───── ❀ ─────╖\n╙───── ❀ ─────╜": "A delicate flower framed by balanced corner brackets.",
    "「───── ⊹ ─────」\n「───── ⊹ ─────」": "A delicate star centered on parallel lines.",
    "╔───── ☆ ─────╗\n╚───── ☆ ─────╝": "Symmetrical box frame with star-centered borders."
  },
  "art-boxes": {
    "╔═══════════════╗\n║   ✦   ✿   ✦   ║\n╚═══════════════╝": "A delicate, centered plaque with floral and star accents.",
    "╭───────────────╮\n│ ⋆ ˚｡⋆ ୨୧ ⋆ ˚｡⋆ │\n╰───────────────╯": "A three-line box with stars and a butterfly bow at its center.",
    "┏━━━━━━━━━━━━━┓\n┃ ♡ · · · · · · · ♡ ┃\n┗━━━━━━━━━━━━━┛": "A heart-lined plaque for a name or short title.",
    "✿❀✿❀✿❀✿❀✿❀✿\n❀ · · · · · · · · · ❀\n✿❀✿❀✿❀✿❀✿❀✿": "A floral box border with soft dotted center.",
    "◤━━━━━━━━━━━◥\n┃   ✦  ❖  ✦   ┃\n◣━━━━━━━━━━━◢": "A diamond-shaped star framed in a bold, bordered plaque.",
    "╔♛═══════════♛╗\n║ ⋆ ✧ ⋆ ✧ ⋆ ✧ ⋆ ║\n╚♛═══════════♛╝": "A regal three-line box with crowned borders and starry inner row.",
    "▛▀▀▀▀▀▀▀▀▀▀▀▜\n▌ ❀ ⊹ ❀ ⊹ ❀ ⊹ ▐\n▙▄▄▄▄▄▄▄▄▄▄▄▟": "A floral box with gentle, whimsical charm.",
    "╭┈┈┈┈┈┈┈┈┈┈┈╮\n┊ ✩ · ♡ · ✩ · ♡ ┊\n╰┈┈┈┈┈┈┈┈┈┈┈╯": "Hearts and stars framed in a delicate dotted box.",
    "┌───────────────┐\n│ ☾ ⋆ ✦ ⋆ ✦ ⋆ ☽ │\n└───────────────┘": "A starry night sky border for a lunar-themed title.",
    "═══════◆═══════\n   ✦ · ✿ · ✦\n═══════◆═══════": "A framed plaque with a centered diamond and flower motif."
  }
};
